// SCR-STAFF-105 — ویزارد ادغام و تفکیک (AP-PAT-02، US-PAT-032): انتخاب رکورد اصلی و ادغام‌شونده، مقایسه فیلد به فیلد با انتخاب
// مقدار هر فیلد متفاوت (AC-PAT-032.1)، شمار داده‌های منتقل‌شونده و مانع (کد ملی متفاوت — PHX-PAT-0304)، دلیل و تأیید. ادغام با پیوند
// است (رکورد ثانویه حذف نمی‌شود) و تا ۹۰ روز از بخش «ادغام‌ها»ی پرونده قابل تفکیک است (BR-PAT-043)؛ AP-PAT-03: فهرست بازبینی
// سوابق پس از ادغام (AC-PAT-033.1).
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type MergeFieldComparison,
  type MergeRecordView,
  type MergeSide,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { errorMessageKey, useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  ListPage,
  LoadingState,
  ReasonDialog,
  toJalali,
  toJalaliDateTime,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { mergePath, patientPath, patKeys } from './common';
import { PatientPicker } from './PatientPicker';
import { PatientResultSummary } from './PatientSearchPalette';
import { TimelineSummary } from './PatientTimeline';

export const SCR_STAFF_105 = 'SCR-STAFF-105';

function usePatientItem(id: string | null) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: [...patKeys.patient(session?.tenantId, id ?? ''), 'item'],
    queryFn: async (): Promise<PatientSearchItem> => {
      const view = await unwrap(tenantApi.GET('/api/v1/patients/patients/{id}', { params: { path: { id: id ?? '' } } }));
      return {
        id: view.id,
        displayName: view.displayName,
        fileNumber: view.fileNumber,
        previousFileNumber: view.previousFileNumber,
        gender: view.gender,
        age: view.age,
        mobileHint: view.contacts.find((contact) => contact.isPrimary)?.value.slice(-4) ?? null,
        lastVisitAt: view.lastVisitAt,
        badges: view.badges,
      };
    },
    enabled: Boolean(id),
  });
}

function display(field: string, value: string | null | undefined, t: (key: string) => string): string {
  if (value === null || value === undefined || value === '') {
    return t('common:states.none');
  }
  if (field === 'birth_date') {
    return toJalali(value);
  }
  const enums: Record<string, string> = { gender: 'gender', marital_status: 'marital', education: 'education', blood_group: 'blood' };
  if (enums[field]) {
    return t(`pat:${enums[field]}.${value}`);
  }
  return field === 'primary_mobile' ? faDigits(value) : value;
}

export function MergeWizardPage() {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const idempotency = useIdempotencyKey();
  const write = useWriteAccess('pat.patient.merge');
  const [params, setParams] = useSearchParams();
  const survivorId = params.get('survivor');
  const mergedId = params.get('merged');
  const survivorItem = usePatientItem(survivorId);
  const mergedItem = usePatientItem(mergedId);
  const [choices, setChoices] = useState<Record<string, MergeSide>>({});
  const [reason, setReason] = useState('');
  const [step, setStep] = useState(0);
  const preview = useQuery({
    queryKey: patKeys.mergePreview(session?.tenantId, survivorId ?? '', mergedId ?? ''),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/patients/patients/{id}/merge-preview', {
          params: { path: { id: survivorId ?? '' }, query: { otherId: mergedId ?? '' } },
        }),
      ),
    enabled: Boolean(survivorId && mergedId),
  });
  useEffect(() => {
    setChoices({});
    setStep(survivorId && mergedId ? 1 : 0);
  }, [survivorId, mergedId]);
  const merge = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}:merge', {
          params: { path: { id: survivorId ?? '' } },
          body: { mergedId, choices, reason: reason.trim() || null },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: patKeys.all(session?.tenantId) });
      navigate(patientPath(result.survivor.id));
    },
  });
  const select = (key: 'survivor' | 'merged', item: PatientSearchItem | null) => {
    const next = new URLSearchParams(params);
    if (item) {
      next.set(key, item.id);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
  };
  const swap = () => {
    if (survivorId && mergedId) {
      setParams(new URLSearchParams(mergePath(mergedId, survivorId).split('?')[1]), { replace: true });
    }
  };
  const different = (preview.data?.fields ?? []).filter((field) => field.different);
  const columns: DataTableColumn<MergeFieldComparison>[] = [
    { id: 'field', headerKey: 'pat:merge.field', cell: (row) => t(`pat:merge.fields.${row.field}`) },
    {
      id: 'choice',
      headerKey: 'pat:merge.choice',
      cell: (row) =>
        row.different ? (
          <RadioGroup
            row
            value={choices[row.field] ?? 'Survivor'}
            onChange={(event) => setChoices((current) => ({ ...current, [row.field]: event.target.value as MergeSide }))}
            aria-label={t(`pat:merge.fields.${row.field}`)}
          >
            <FormControlLabel value="Survivor" control={<Radio />} label={display(row.field, row.survivorValue, t)} />
            <FormControlLabel value="Merged" control={<Radio />} label={display(row.field, row.mergedValue, t)} data-testid={`merge-choice-${row.field}`} />
          </RadioGroup>
        ) : (
          display(row.field, row.survivorValue, t)
        ),
    },
  ];
  const blocking = preview.data?.blocking;

  return (
    <ListPage screenId={SCR_STAFF_105} titleKey="pat:merge.title" subtitleKey="pat:merge.subtitle">
      <Stack spacing={2}>
        <Stepper activeStep={step} alternativeLabel>
          {['select', 'compare', 'confirm'].map((key) => (
            <Step key={key}>
              <StepLabel>{t(`pat:merge.steps.${key}`)}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Stack sx={{ flex: 1 }} spacing={0.5}>
              <PatientPicker
                labelKey="pat:merge.survivor"
                value={survivorItem.data ?? null}
                onChange={(item) => select('survivor', item)}
                excludeIds={mergedId ? [mergedId] : []}
                testId="merge-survivor"
              />
              {survivorItem.data && (
                <Typography variant="caption" color="text.secondary">
                  <PatientResultSummary item={survivorItem.data} />
                </Typography>
              )}
            </Stack>
            <Button startIcon={<SwapHorizIcon />} onClick={swap} disabled={!survivorId || !mergedId} aria-label={t('pat:merge.swap')}>
              {t('pat:merge.swap')}
            </Button>
            <Stack sx={{ flex: 1 }} spacing={0.5}>
              <PatientPicker
                labelKey="pat:merge.merged"
                value={mergedItem.data ?? null}
                onChange={(item) => select('merged', item)}
                excludeIds={survivorId ? [survivorId] : []}
                testId="merge-merged"
              />
              {mergedItem.data && (
                <Typography variant="caption" color="text.secondary">
                  <PatientResultSummary item={mergedItem.data} />
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
        {survivorId && mergedId && (
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="merge-preview">
            {preview.isPending ? (
              <LoadingState rows={3} />
            ) : preview.error ? (
              <ErrorState error={preview.error} onRetry={() => void preview.refetch()} />
            ) : (
              <Stack spacing={2}>
                {blocking && (
                  <Alert severity="error" data-testid="merge-blocking">
                    {t(errorMessageKey(blocking, 409))}
                  </Alert>
                )}
                <Typography variant="body2">
                  {different.length > 0 ? t('pat:merge.different', { count: different.length }) : t('pat:merge.same')}
                </Typography>
                <DataTable captionKey="pat:merge.compare" columns={columns} rows={preview.data.fields} getRowId={(row) => row.field} />
                <Typography variant="subtitle1">{t('pat:merge.collected')}</Typography>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }} data-testid="merge-collected">
                  {Object.entries(preview.data.collected).map(([key, count]) => (
                    <Typography key={key} variant="body2">
                      {t(`pat:merge.collected_items.${key}`, { defaultValue: key })}: {faDigits(count)}
                    </Typography>
                  ))}
                </Stack>
                <Alert severity="info">{t('pat:merge.notice')}</Alert>
                <TextField
                  label={t('pat:merge.reason')}
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                    setStep(2);
                  }}
                  fullWidth
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                />
                {merge.error ? <ErrorState error={merge.error} compact /> : null}
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={!write.canWrite || Boolean(blocking) || merge.isPending}
                    onClick={() => merge.mutate()}
                    data-testid="merge-confirm"
                  >
                    {t('pat:merge.confirm')}
                  </Button>
                  <Button component={RouterLink} to={patientPath(survivorId)}>
                    {t('common:actions.cancel')}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Paper>
        )}
      </Stack>
    </ListPage>
  );
}

/** بخش «ادغام‌ها» در پرونده: سابقه و تفکیک تا ۹۰ روز (US-PAT-032، BR-PAT-043). */
export function PatientMergesSection({ patientId, mergedIntoId }: { patientId: string; mergedIntoId: string | null }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const access = useAccess();
  const unmergeAccess = useWriteAccess('pat.patient.unmerge');
  const [unmerging, setUnmerging] = useState<MergeRecordView | null>(null);
  const [reviewing, setReviewing] = useState<MergeRecordView | null>(null);
  const merges = useQuery({
    queryKey: patKeys.merges(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/merges', { params: { path: { id: patientId } } })),
  });
  const unmerge = useMutation({
    mutationFn: ({ merge, reason }: { merge: MergeRecordView; reason: string }) =>
      unwrap(tenantApi.POST('/api/v1/patients/patients/{id}:unmerge', { params: { path: { id: merge.mergedId } }, body: { reason } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: patKeys.all(session?.tenantId) });
      setUnmerging(null);
    },
  });
  const columns: DataTableColumn<MergeRecordView>[] = [
    { id: 'at', headerKey: 'pat:merge.merged_at', cell: (row) => toJalaliDateTime(row.mergedAt) },
    {
      id: 'merged',
      headerKey: 'pat:merge.merged',
      cell: (row) => (
        <RouterLink to={patientPath(row.mergedId)}>
          {row.mergedDisplayName}
          {row.mergedFileNumber ? ` (${faDigits(row.mergedFileNumber)})` : ''}
        </RouterLink>
      ),
    },
    {
      id: 'state',
      headerKey: 'pat:merge.state',
      cell: (row) =>
        row.unmergedAt
          ? t('pat:merge.unmerged_at', { date: toJalaliDateTime(row.unmergedAt) })
          : t('pat:merge.deadline', { date: toJalali(row.unmergeDeadline) }),
    },
    {
      id: 'actions',
      headerKey: 'pat:merge.unmerge',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {access.can('pat.patient.unmerge') && (
            <Button size="small" onClick={() => setReviewing(row)} data-testid="unmerge-review">
              {t('pat:merge.review')}
            </Button>
          )}
          {unmergeAccess.canWrite && row.canUnmerge ? (
            <Button
              size="small"
              color="warning"
              onClick={() => {
                setReviewing(row);
                setUnmerging(row);
              }}
              data-testid="unmerge"
            >
              {t('pat:merge.unmerge')}
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];
  const items = merges.data?.items ?? [];
  if (!mergedIntoId && merges.isSuccess && items.length === 0 && !access.can('pat.patient.merge')) {
    return null;
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="merges-section">
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {t('pat:merge.history')}
        </Typography>
        {!mergedIntoId && access.can('pat.patient.merge') && (
          <Button component={RouterLink} to={mergePath(patientId)} data-testid="merge-start">
            {t('pat:merge.start')}
          </Button>
        )}
      </Stack>
      {mergedIntoId && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="merged-into">
          {t('pat:merge.merged_into')} <RouterLink to={patientPath(mergedIntoId)}>{t('pat:merge.open_survivor')}</RouterLink>
        </Alert>
      )}
      <DataTable
        captionKey="pat:merge.history"
        columns={columns}
        rows={items}
        isLoading={merges.isPending}
        error={merges.error}
        getRowId={(row) => row.id}
        emptyTitleKey="pat:merge.empty"
      />
      {reviewing && <UnmergeReviewList patientId={patientId} mergeId={reviewing.id} onClose={() => setReviewing(null)} />}
      <ReasonDialog
        open={unmerging !== null}
        titleKey="pat:merge.unmerge"
        messageKey="pat:merge.unmerge_confirm"
        busy={unmerge.isPending}
        error={unmerge.error}
        onConfirm={(reason) => unmerging && unmerge.mutate({ merge: unmerging, reason })}
        onCancel={() => setUnmerging(null)}
      />
    </Paper>
  );
}

/**
 * فهرست بازبینی تفکیک (AC-PAT-033.1 — C-AGENT-1353): سوابقی که پس از ادغام روی پرونده اصلی ثبت شده‌اند با رکورد اصلی می‌مانند و برای
 * بازبینی دستی نمایش داده می‌شوند (پیش و پس از تفکیک).
 */
export function UnmergeReviewList({ patientId, mergeId, onClose }: { patientId: string; mergeId: string; onClose: () => void }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const review = useQuery({
    queryKey: patKeys.unmergeReview(session?.tenantId, patientId, mergeId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/patients/patients/{id}/merges/{mergeId}/review', {
          params: { path: { id: patientId, mergeId } },
        }),
      ),
  });
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1.5 }} data-testid="unmerge-review-list">
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h3" sx={{ flexGrow: 1 }}>
          {t('pat:merge.review_title')}
        </Typography>
        <Button size="small" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('pat:merge.review_hint')}
      </Typography>
      {review.isPending && <LoadingState />}
      {review.error ? <ErrorState error={review.error} compact onRetry={() => void review.refetch()} /> : null}
      {review.data && review.data.partial.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('pat:merge.review_partial', { modules: review.data.partial.join(t('pat:list_separator')) })}
        </Alert>
      )}
      {review.data && review.data.items.length === 0 && (
        <Typography variant="body2" data-testid="unmerge-review-empty">
          {t('pat:merge.review_empty')}
        </Typography>
      )}
      {review.data && review.data.items.length > 0 && (
        <List dense>
          {review.data.items.map((item) => (
            <ListItem key={`${item.source}-${item.id}`} disableGutters data-testid="unmerge-review-item">
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} component="span">
                    <span>{t(item.titleKey)}</span>
                    <TimelineSummary item={item} />
                  </Stack>
                }
                secondary={toJalaliDateTime(item.occurredAt)}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
}
